import { CheckCircle2, Clock3, RefreshCw, Landmark } from "lucide-react";

const fmt = (v) => {
  if (!v) return null;
  try {
    return new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return null;
  }
};

const inr = (n) => (n == null ? null : `₹${Number(n).toFixed(0)}`);

// Customer-facing refund progress. `tracker` is the backend refund_tracker object
// ({ state, partial, amount, method, reference, failed, initiated_at, credited_at, note }).
export default function RefundTracker({ tracker }) {
  if (!tracker) return null;
  const credited = tracker.state === "credited";
  const amt = inr(tracker.amount);

  return (
    <div className="rounded-2xl border border-surface-300 bg-white p-4" data-testid="refund-tracker">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-ink">
          {tracker.partial ? "Partial refund" : "Refund"}
          {amt ? <span className="text-ink-soft font-semibold"> · {amt}</span> : null}
        </p>
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${credited ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}
          data-testid="refund-tracker-state"
        >
          {credited ? "Credited" : "In progress"}
        </span>
      </div>

      <ol className="mt-3 space-y-3">
        <li className="flex items-start gap-3" data-testid="refund-step-initiated">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
          <div>
            <p className="text-sm font-semibold text-ink">Refund initiated</p>
            {fmt(tracker.initiated_at) && <p className="text-xs text-ink-muted">{fmt(tracker.initiated_at)}</p>}
          </div>
        </li>
        <li className="flex items-start gap-3" data-testid="refund-step-credited">
          {credited ? (
            <Landmark className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
          ) : (
            <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          )}
          <div>
            <p className={`text-sm font-semibold ${credited ? "text-ink" : "text-ink-soft"}`}>
              {credited ? `Credited to your ${tracker.method === "cash" ? "account" : "original payment method"}` : "Processing with your bank"}
            </p>
            <p className="text-xs text-ink-muted">
              {credited
                ? fmt(tracker.credited_at) || "Done"
                : "Online refunds usually reach your account in 5–7 business days."}
            </p>
          </div>
        </li>
      </ol>

      {tracker.failed && !credited && (
        <p className="mt-3 flex items-center gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="refund-retry-note">
          <RefreshCw className="h-3.5 w-3.5" /> The first attempt didn't go through — we're retrying your refund automatically.
        </p>
      )}

      {tracker.reference && (
        <p className="mt-2 text-xs text-ink-muted" data-testid="refund-reference">Reference: {tracker.reference}</p>
      )}
    </div>
  );
}
